import { deleteIn, getIn, setIn } from '../../../helpers/helpers';
import { getGuid } from '../../../helpers/utility';
import { FieldValidation, ValidationSettingsPosition } from '../../../types/template-types';
import { getDataLayoutPositionKeyPathsByFieldId, getField, getFieldKeyPathById, getFieldPositionKeyPathsByFieldId } from '../../selectors';
import { TemplateState, TemplateEditorPayload, KeyPath } from '../../templateeditor-types';
import { updatedSelectionBoundingRectangle } from '../../updaters';

export function removeTemplateValidations(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId } = payload;

  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const fieldValidationKeyPath = [...fieldKeyPath, 'validationSettings'];

  deleteIn(state, fieldValidationKeyPath);

  return state;
}

export function saveTemplateValidations(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId, fieldValidation } = payload;

  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const validationSettingsKeyPath = [...fieldKeyPath, 'validationSettings'];

  // We only support 1 validation so we will overwrite the existing validation
  // const existingValidationSettings = getIn<ValidationSettings[]>(state, validationSettingsKeyPath) ?? [];
  // existingValidationSettings.push(templateValidationValue);

  const id = getGuid(); // Only on save, we will generate a new id
  const validationNumber = 0;

  fieldValidation.id = id;
  fieldValidation.validationNumber = validationNumber;

  setIn(state, validationSettingsKeyPath, [fieldValidation]);

  // now add validationPositions for each fieldPosition
  const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);

  for (const fieldPositionKeyPath of fieldPositionKeyPaths) {
    const validationSettingsPositionsKeyPath: KeyPath = [...fieldPositionKeyPath, 'validationSettings'];
    const validationPositions = getIn<ValidationSettingsPosition[]>(state, validationSettingsPositionsKeyPath) ?? [];

    validationPositions.push({
      id,
      validationNumber
    });

    setIn(state, validationSettingsPositionsKeyPath, validationPositions);
  }

  return state;
}

export function addTemplateValidation(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldIds } = payload;
  const fieldValidations: FieldValidation[] = [];
  const fieldPositionKeyPaths: KeyPath[] = [];
  const dataLayoutPositionKeyPaths: KeyPath[] = [];
  const formView = state.currentTemplateView === 0;

  fieldIds.forEach((fieldId) => {
    const type = getField(state, fieldId)?.type;
    fieldValidations.push({ id: fieldId, type });

    if (formView) {
      fieldPositionKeyPaths.push(...getFieldPositionKeyPathsByFieldId(state, fieldId));
    } else {
      dataLayoutPositionKeyPaths.push(...getDataLayoutPositionKeyPathsByFieldId(state, fieldId));
    }
  });

  if (formView) {
    state.selection = { fieldPositions: fieldPositionKeyPaths, fieldIds, fieldValidations };
  } else {
    state.selection = { dataLayoutPositions: dataLayoutPositionKeyPaths, fieldIds, fieldValidations };
  }

  updatedSelectionBoundingRectangle(state);
  return state;
}

export function editTemplateValidation(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldIds } = payload;
  const fieldValidations: FieldValidation[] = [];
  const fieldPositionKeyPaths: KeyPath[] = [];
  const dataLayoutPositionKeyPaths: KeyPath[] = [];
  const formView = state.currentTemplateView === 0;

  fieldIds.forEach((fieldId) => {
    const type = getField(state, fieldId)?.type;
    fieldValidations.push({ id: fieldId, type });

    if (formView) {
      fieldPositionKeyPaths.push(...getFieldPositionKeyPathsByFieldId(state, fieldId));
    } else {
      dataLayoutPositionKeyPaths.push(...getDataLayoutPositionKeyPathsByFieldId(state, fieldId));
    }
  });

  if (formView) {
    state.selection = { fieldPositions: fieldPositionKeyPaths, fieldIds, fieldValidations };
  } else {
    state.selection = { dataLayoutPositions: dataLayoutPositionKeyPaths, fieldIds, fieldValidations };
  }

  updatedSelectionBoundingRectangle(state);

  return state;
}

export function cancelTemplateValidation(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldIds } = payload;
  const fieldValidations: FieldValidation[] = [];
  const fieldPositionKeyPaths: KeyPath[] = [];
  const dataLayoutPositionKeyPaths: KeyPath[] = [];
  const formView = state.currentTemplateView === 0;

  fieldIds.forEach((fieldId) => {
    fieldValidations.push({ id: fieldId });

    if (formView) {
      fieldPositionKeyPaths.push(...getFieldPositionKeyPathsByFieldId(state, fieldId));
    } else {
      dataLayoutPositionKeyPaths.push(...getDataLayoutPositionKeyPathsByFieldId(state, fieldId));
    }
  });

  if (formView) {
    state.selection = { fieldPositions: fieldPositionKeyPaths, fieldIds };
  } else {
    state.selection = { dataLayoutPositions: dataLayoutPositionKeyPaths, fieldIds };
  }

  updatedSelectionBoundingRectangle(state);

  return state;
}

export function updateSelectedValidation(state: TemplateState, payload: TemplateEditorPayload): TemplateState {
  const { fieldId, type } = payload;
  const fieldValidation = { id: fieldId, type };

  // TODO update background color of error field

  const formView = state.currentTemplateView === 0;

  if (formView) {
    const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, fieldId);
    state.selection = { fieldPositions: fieldPositionKeyPaths, fieldIds: [fieldId], fieldValidations: [fieldValidation] };
  } else {
    const dataLayoutPositionKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, fieldId);
    state.selection = { dataLayoutPositions: dataLayoutPositionKeyPaths, fieldIds: [fieldId], fieldValidations: [fieldValidation] };
  }

  updatedSelectionBoundingRectangle(state);

  return state;
}
